import { extend, get } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import CommandsApi from 'client/api/v2/CommandsApi';
import Vue from 'vue';
import async from 'async';

export const createCommandsCollection = (Api = CommandsApi, _id = '_id', name = 'name') => {
  const collection = new CollectionModule(
    Api,
    _id,
    name,
  );
  const commandCollectionModule = collection.extractModule();

  const getInitialState = () => {
    const { state } = commandCollectionModule;
    state.features.commandQueueIds = {};
    return {
      ...state,
    };
  };

  return {
    namespaced: true,

    state: getInitialState(),

    getInitialState,

    getters: {
      ...commandCollectionModule.getters,

      getItemsToBind(state = { features: { selectedItems: { ids: [] } } }) {
        return state.features.selectedItems.ids.map(item => ({ id: item }));
      },

    },

    actions: {
      ...commandCollectionModule.actions,

      /**
       * Cancel a queued command with the given command ID
       */
      cancelQueuedCommands({ commit, state }, id) {
        const queueIds = state.features.commandQueueIds[id] || [];
        return CommandsApi.cancelQueuedCommands(queueIds)
          .then((res) => {
            commit('REMOVE_FROM_COMMAND_QUEUE', { id });
            return get(res, 'data.count', 0);
          });
      },

      // Check all queued commands and remove them from queue if they are no longer in queue
      checkQueuedCommands({ commit, state }) {
        const commandIdsRemovedFromQueue = [];

        // Generate all of the check functions so we can run them in parallel...
        const commandIds = Object.keys(state.features.commandQueueIds);
        const checkFuncs = commandIds.map(id => (cb) => {
          const queueIds = state.features.commandQueueIds[id];
          return CommandsApi.checkQueuedCommands(queueIds)
            .then((res) => {
              const newQueueIds = get(res, 'queueIds', []);
              if (!newQueueIds.length) {
                commit('REMOVE_FROM_COMMAND_QUEUE', { id });
                commandIdsRemovedFromQueue.push(id);
              }
              cb();
            })
            .catch(err => cb(err));
        });

        // Run all the command updates in parallel and don't start another update until we get
        // finished responses back from all queued commands...
        return new Promise((resolve, reject) => {
          async.parallel(checkFuncs, (err) => {
            if (err) return reject(err);
            return resolve(commandIdsRemovedFromQueue);
          });
        });
      },

      // Run a command with the given command ID
      runCommand({ commit, getters }, id) {
        const command = getters.getItem(id);
        return CommandsApi.runCommand(command)
          .then((res) => {
            // If we have queue ids, add them to the list (otherwise we can remove the running
            // overlay from this command because there's nothing to do here)...
            const queueIds = get(res, 'data.queueIds', []);
            if (queueIds.length) {
              commit('ADD_TO_COMMAND_QUEUE', { id, queueIds });
            }
            return queueIds;
          })
          .catch((err) => {
            throw new Error(`${err}: Unable to run the '${command.name}' command.`);
          });
      },

      itemLaunchType({ getters }, id) {
        const command = getters.getItem(id);
        return command.launchType;
      },
    },

    mutations: {
      ...commandCollectionModule.mutations,

      ADD_TO_COMMAND_QUEUE(state, { id, queueIds }) {
        Vue.set(state.features.commandQueueIds,
          id,
          extend([], state.features.commandQueueIds[id] || [],
            queueIds));
      },

      REMOVE_FROM_COMMAND_QUEUE(state, { id }) {
        Vue.delete(state.features.commandQueueIds[id]);
      },

    },
  };
};

export default createCommandsCollection();
